import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import {
  SignupOnboardingService
} from '../../../services/signup-onboarding/signup-onboarding.service';
import { AuthService } from '../../../services/account/auth.service';
import { ParentsInformationService } from '../../../services/parents-information/parents-information.service';
import { OnboardingFormData } from '../../../models/onboarding/onboardingFormData';
import { UpdateUserProfileRequest } from '../../../models/account-centre/updateUserProfileRequest';
import { ParentsInformationRequest } from '../../../models/parents-information/parentsInformationRequest';
import { ParentsRelation } from '../../../models/enums/parentsRelation';
import { forkJoin } from 'rxjs';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { UtilityService } from '../../../services/utility/utility.service';
import { Prediction } from '../../../models/country/olaPlacesResponse';
import { Gender, GenderDescriptions } from '../../../models/enums/enums';
import { CourseLevelEnum, CourseLevelEnumDescriptions } from '../../../models/enums/courseLevelEnum';
import { LottieAnimationComponent } from '../lottie-animation/lottie-animation.component';

@Component({
  selector: 'app-onboarding-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CalendarModule, DropdownModule, MultiSelectModule, AutoCompleteModule, LottieAnimationComponent],
  templateUrl: './onboarding-modal.component.html',
  styleUrl: './onboarding-modal.component.scss',
})
export class OnboardingModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private onboardingService = inject(SignupOnboardingService);
  private authService = inject(AuthService);
  private parentsService = inject(ParentsInformationService);

  // ── Visibility & Step State ──────────────────────────────────────────────
  isVisible = false;
  currentStep = 1;
  isAnimating = false;
  animDirection: 'forward' | 'backward' = 'forward';

  // ── API State ────────────────────────────────────────────────────────────
  isSubmitting = false;
  submissionError = '';

  // ── Country pills ────────────────────────────────────────────────────────
  countryPills = ['USA', 'UK','India', 'Europe', 'Singapore', 'UAE', 'NZ', 'Canada', 'Australia', 'Other'];
  selectedCountries: string[] = [];

  // ── Form Groups ──────────────────────────────────────────────────────────
  aboutYouForm!: FormGroup;
  dreamDestinationForm!: FormGroup;
  parentDetailsForm!: FormGroup;

  // ── Dropdown Data ────────────────────────────────────────────────────────
  genderOptions = [
    { name: GenderDescriptions[Gender.Male], value: Gender.Male },
    { name: GenderDescriptions[Gender.Female], value: Gender.Female },
    { name: GenderDescriptions[Gender.Other], value: Gender.Other }
  ];

  locationSuggestions: any[] = [];
  selectedLatitude: number | null = null;
  selectedLongitude: number | null = null;
  selectedParentIndex = 0;
  isFromAppForm = false;
  userId: string | null = null;

  levelOptions = [
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.BachelorsDegree], key: CourseLevelEnum.BachelorsDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.MastersDegree], key: CourseLevelEnum.MastersDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.DoctoralDegree], key: CourseLevelEnum.DoctoralDegree },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Diploma], key: CourseLevelEnum.Diploma },
    { name: CourseLevelEnumDescriptions[CourseLevelEnum.Certificate], key: CourseLevelEnum.Certificate }
  ];

  fieldOfInterestOptions = [
    'Computer Science & IT',
    'Engineering & Technology',
    'Business & Management',
    'Medicine & Healthcare',
    'Law & Legal Studies',
    'Arts & Design',
    'Natural Sciences',
    'Social Sciences',
    'Architecture',
    'Education',
    'Finance & Economics',
    'Media & Communications',
    'Other',
  ];

  journeyStageOptions = [
    { name: 'An Early Explorer', key: 'An Early Explorer', icon: '../../../assets/icons/telescope.png'},
    { name: 'Finalising Shortlist', key: 'Finalising Shortlist', icon: '../../../assets/icons/target.png' },
    { name: 'Ready-to-Apply', key: 'Ready-to-Apply', icon: '../../../assets/icons/touch-screen.png' }
  ];

  enrollmentYearOptions: any[] = [];

  private readonly utilityService = inject(UtilityService);

  // ────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.initForms();
    this.setCollegeStartTimings();
    this.checkAndShowModal();
    this.subscribeToRouterEvents();
  }

  private subscribeToRouterEvents(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkAndShowModal();
    });
  }

  private setCollegeStartTimings(): void {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    this.enrollmentYearOptions = [];
    const maxYear = currentYear + 4;
    
    for (let year = currentYear; year <= maxYear; year++) {
      const springDeadlineYear = year - 1;
      const springDeadlineMonth = 10;
      const fallDeadlineYear = year;
      const fallDeadlineMonth = 3;
      
      const springDeadlinePassed = (currentYear > springDeadlineYear) || 
        (currentYear === springDeadlineYear && currentMonth > springDeadlineMonth);
      
      if (!springDeadlinePassed) {
        this.enrollmentYearOptions.push({ name: `Spring ${year}`, key: `Spring ${year}` });
      }
      
      const fallDeadlinePassed = (currentYear > fallDeadlineYear) || 
        (currentYear === fallDeadlineYear && currentMonth > fallDeadlineMonth);
      
      if (!fallDeadlinePassed) {
        this.enrollmentYearOptions.push({ name: `Fall ${year}`, key: `Fall ${year}` });
      }
    }
  }

  checkAndShowModal(): void {
    const currentUrl = this.router.url;
    if (!currentUrl.includes('/dashboard')) {
      if (this.isVisible) {
        this.isVisible = false;
        this.updateBodyScroll();
      }
      return;
    }

    const tree = this.router.parseUrl(currentUrl);
    if (tree.queryParams['appFormSuccess'] === 'true') {
      this.isFromAppForm = true;
      this.currentStep = 5;

      const raw = localStorage.getItem('account');
      if (raw) {
        const account = JSON.parse(raw);
        this.userId = account.userId;
      }

      if (!this.isVisible) {
        this.isVisible = true;
        this.updateBodyScroll();
      }
      return;
    }

    try {
      const raw = localStorage.getItem('account');
      if (raw) {
        const account = JSON.parse(raw);
        if (account?.isNewUser === true) {
          if (this.isVisible) return;
          const userId = account.userId;
          if (!userId) return;

          forkJoin({
            user: this.authService.getUserByid(userId),
            parents: this.parentsService.getParentsInformationByStudentId(userId)
          }).subscribe({
            next: (res) => {
              const user = res.user;
              const parents = res.parents.result;

              if (user) {
                this.aboutYouForm.patchValue({
                  firstName: user.firstName,
                  lastName: user.lastName,
                  dateOfBirth: user.bornOn ? new Date(user.bornOn) : null,
                  gender: user.gender,
                  currentLocation: user.location ? { description: user.location } : null
                });
                this.selectedLatitude = user.latitude ?? null;
                this.selectedLongitude = user.longitude ?? null;

                this.dreamDestinationForm.patchValue({
                  intendedCountries: user.destination || [],
                  intendedLevel: user.degreePreference,
                  fieldOfInterest: user.intendedMajor,
                  journeyStage: user.journeyStatus,
                  enrollmentYear: user.collageStartOn
                });
                this.selectedCountries = user.destination || [];
              }

              const hasDegreePref = user.degreePreference !== null && user.degreePreference !== undefined;
              const isDemographyFilled = !!(user.firstName && user.lastName) && hasDegreePref;
              const isParentsFilled = parents && parents.length > 0;

              if (isDemographyFilled) {
                if (isParentsFilled) {
                  this.markOnboardingComplete();
                  return;
                } else {
                  this.currentStep = 4;
                }
              } else {
                this.currentStep = 1;
              }

              setTimeout(() => {
                if (this.router.url.includes('/dashboard')) {
                  this.isVisible = true;
                  this.updateBodyScroll();
                }
              }, 800);
            },
            error: () => {
              this.currentStep = 1;
              setTimeout(() => {
                if (this.router.url.includes('/dashboard')) {
                  this.isVisible = true;
                  this.updateBodyScroll();
                }
              }, 800);
            }
          });
        }
      }
    } catch {
      // silent
    }
  }

  initForms(): void {
    const alphaPattern = '^[a-zA-Z ]*$';

    this.aboutYouForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(alphaPattern)]],
      lastName: ['', [Validators.required, Validators.pattern(alphaPattern)]],
      dateOfBirth: ['', Validators.required],
      gender: [null, Validators.required],
      currentLocation: ['', Validators.required],
    });

    this.dreamDestinationForm = this.fb.group({
      intendedCountries: [[]],
      intendedLevel: [null],
      fieldOfInterest: [null],
      journeyStage: [null],
      enrollmentYear: [null],
    });

    this.parentDetailsForm = this.fb.group({
      fatherName: ['', [Validators.pattern(alphaPattern)]],
      fatherEmail: ['', [Validators.email]],
      fatherPhone: ['', [Validators.pattern(/^[0-9]{10}$/)]],
      motherName: ['', [ Validators.pattern(alphaPattern)]],
      motherEmail: ['', [Validators.email]],
      motherPhone: ['', [Validators.pattern(/^[0-9]{10}$/)]],
    });
  }


  goToStep(step: number): void {
    if (this.isAnimating) return;
    this.animDirection = step > this.currentStep ? 'forward' : 'backward';
    this.isAnimating = true;
    setTimeout(() => {
      this.currentStep = step;
      this.isAnimating = false;
      const container = document.querySelector('.modal-container');
      if (container) container.scrollTop = 0;
    }, 280);
  }

  onLetsGetStarted(): void {
    this.goToStep(2);
  }

  onNextStep(): void {
    if (this.currentStep === 2) {
      if (this.aboutYouForm.invalid) {
        this.aboutYouForm.markAllAsTouched();
        return;
      }
      this.goToStep(3);
      return;
    }

    if (this.currentStep === 3) {
      if (this.dreamDestinationForm.invalid) {
        this.dreamDestinationForm.markAllAsTouched();
        return;
      }
      this.goToStep(4);
      return;
    }

    if (this.currentStep === 4) {
      this.onSaveAndContinue(true);
    }
  }

  onSkipParents(): void {
    this.onSaveAndContinue(false);
  }

  onSkipDreamDestination(): void {
    this.goToStep(4);
  }

  onSaveAndContinue(includeParents: boolean): void {
    if (includeParents) {
      if (this.parentDetailsForm.invalid) {
        this.parentDetailsForm.markAllAsTouched();
        return;
      }
    }

    this.isSubmitting = true;
    this.submissionError = '';

    const userId = this.getUserId();
    const ay = this.aboutYouForm.value;
    const dd = this.dreamDestinationForm.value;
    const pd = this.parentDetailsForm.value;

    let formattedDob: string | null = null;
    if (ay.dateOfBirth) {
      const dob = new Date(ay.dateOfBirth);
      formattedDob = `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`;
    }

    // 1. Prepare User Profile Payload
    const profilePayload: UpdateUserProfileRequest = {
      firstName: ay.firstName?.trim() || '',
      lastName: ay.lastName?.trim() || '',
      bornOn: formattedDob,
      destination: [...this.selectedCountries],
      degreePreference: dd.intendedLevel,
      collageStartOn: dd.enrollmentYear,
      gender: Number(ay.gender),
      journeyStatus: dd.journeyStage,
      location: typeof ay.currentLocation === 'string'
        ? ay.currentLocation
        : ay.currentLocation?.description || '',
      latitude: this.selectedLatitude,
      longitude: this.selectedLongitude,
    };

    const calls = [this.onboardingService.updateDemography(profilePayload)];

    // 2. Prepare Parent Info Payloads
    if (includeParents) {
      if (pd.fatherEmail) {
        calls.push(this.onboardingService.createParentInfo({
          studentId: userId,
          name: pd.fatherName?.trim() || null,
          email: pd.fatherEmail?.trim(),
          phone: pd.fatherPhone?.trim() || null,
          relation: ParentsRelation.Father
        }));
      }
      if (pd.motherEmail) {
        calls.push(this.onboardingService.createParentInfo({
          studentId: userId,
          name: pd.motherName?.trim() || null,
          email: pd.motherEmail?.trim(),
          phone: pd.motherPhone?.trim() || null,
          relation: ParentsRelation.Mother
        }));
      }
    }

    forkJoin(calls).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.markOnboardingComplete();
        this.goToStep(5);
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.submissionError = err?.error?.message || 'Something went wrong. Please try again.';
      },
    });
  }

  searchLocation(event: any): void {
    this.utilityService.getOLAPlaces(event.query).subscribe({
      next: (response) => {
        this.locationSuggestions = response?.predictions || [];
      },
      error: () => {
        this.locationSuggestions = [];
      },
    });
  }

  onLocationSelect(event: any): void {
    const prediction = event.value as Prediction;
    if (prediction?.geometry?.location) {
      this.selectedLatitude = prediction.geometry.location.lat;
      this.selectedLongitude = prediction.geometry.location.lng;
    }
  }


  toggleCountry(country: string): void {
    const idx = this.selectedCountries.indexOf(country);
    if (idx > -1) {
      this.selectedCountries.splice(idx, 1);
    } else {
      this.selectedCountries.push(country);
    }
    this.dreamDestinationForm.patchValue({
      intendedCountries: [...this.selectedCountries]
    });
  }

  isCountrySelected(country: string): boolean {
    return this.selectedCountries.includes(country);
  }

  closeModal(): void {
    if (this.currentStep !== 5) return;
    this.isVisible = false;
    this.updateBodyScroll();
  }

  navigateTo(path: string): void {
    this.isVisible = false;
    this.updateBodyScroll();
    this.router.navigate([path]);
  }

  private updateBodyScroll(): void {
    if (this.isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }

  private getUserId(): string {
    try {
      const raw = localStorage.getItem('account');
      return raw ? JSON.parse(raw)?.userId ?? '' : '';
    } catch {
      return '';
    }
  }


  private markOnboardingComplete(): void {
    try {
      const raw = localStorage.getItem('account');
      if (raw) {
        const account = JSON.parse(raw);
        account.isNewUser = false;
        localStorage.setItem('account', JSON.stringify(account));
      }
    } catch {
    }
  }

  isFieldInvalid(form: FormGroup, field: string): boolean {
    const ctrl = form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }
}